<script setup lang="ts">
  import { computed } from 'vue'
  import { ChipsColorI } from '@/typescript/types/ui-components/BaseChipsI'

  import BfBadge from './BfBadge.vue'
  import type { BfBadgeVariant } from './BfBadgeTypes'

  /**
   * Service and invoice status, drawn as the design system's Badge (03-components/07).
   *
   * This component owns no paint of its own: it is a mapping, colour tone in, badge tone out.
   * The `tone` prop still mirrors `ChipsColorI` so it stays a drop-in for BaseChips inside
   * redesigned tables, while what renders is the badge vocabulary the design system actually
   * ships — the `.ticket-status-badge` block this used to draw was retired from the system
   * in 1.1.15 and has no card to check against any more.
   */
  const props = withDefaults(
    defineProps<{
      tone?: ChipsColorI
      /**
       * Names the badge tone outright, bypassing the `tone` mapping. The support ticket register
       * draws its statuses from the badge's bordered family, which the `ChipsColorI` vocabulary —
       * a service/invoice palette — has no word for.
       */
      variant?: BfBadgeVariant
      label: string
      ariaLabel?: string
      /**
       * Opts the green tone out of the subtle fill. A live service is ambient and stays pale;
       * a settled fact — a Paid invoice — is drawn solid, which is what My Invoices asks for.
       */
      solid?: boolean
    }>(),
    {
      tone: 'orange',
      variant: undefined,
      ariaLabel: undefined,
      solid: false,
    },
  )

  /**
   * The six statuses the Dashboard design names, plus the tones it does not.
   * `blue` and `outline_turquoise` are the informational bucket (Completed, Awaiting
   * Configuration); `dark_orange` joins the settled bucket with Cancelled and Suspended,
   * because Terminated and Transferred Away are ended states, not warnings.
   */
  const BADGE_BY_TONE: Record<ChipsColorI, BfBadgeVariant> = {
    green: 'success',
    orange: 'pending',
    red: 'error',
    dark_blue: 'grey',
    pink: 'grey',
    dark_orange: 'grey',
    blue: 'information',
    outline_turquoise: 'information',
    gray: 'light-grey',
    light_gray: 'light-grey',
  }

  const badgeVariant = computed(() => props.variant ?? BADGE_BY_TONE[props.tone] ?? 'light-grey')

  /** Active is the one tone the design draws subtle — a live service should not shout. */
  const isSubtle = computed(() => !props.variant && props.tone === 'green' && !props.solid)
</script>

<template>
  <BfBadge :variant="badgeVariant" :subtle="isSubtle" :aria-label="ariaLabel">{{ label }}</BfBadge>
</template>

<style scoped lang="scss">
  /* The design system's badge does not declare this, and should not — its own card only
     ever shows short labels. A status in a table column does wrap ("Awaiting
     Configuration" over two lines), so keeping it on one line is this component's
     concern rather than the badge block's. */
  .badge {
    white-space: nowrap;
  }
</style>
